import React from 'react';
import clsx from 'clsx';
import { WatermarkLogo, WatermarkPercent } from './Watermarks';

export type WatermarkType = 'Logo' | 'Percentage' | 'Percent';

export interface WatermarkProps {
    type?: WatermarkType;
    /**
     * CSS module styles object containing watermark-specific classes
     */
    styles?: {
        watermark?: string;
        watermarkLogo?: string;
        watermarkPercent?: string;
        [key: string]: string | undefined;
    };
    className?: string;
    /**
     * Additional props to pass to the watermark SVG
     */
    [key: string]: any;
}

/**
 * Reusable Watermark component that handles the logic for displaying different watermark types
 * while preserving component-specific styling through the styles prop.
 *
 * @example
 * ```tsx
 * <Watermark
 *   type="Logo"
 *   styles={styles}
 *   className={styles.customWatermark}
 * />
 * ```
 */
export const Watermark: React.FC<WatermarkProps> = ({ type, styles = {}, className, ...rest }) => {
    // Return null if no watermark type is specified
    if (!type) return null;

    // Handle different watermark type variations
    const isLogo = type === 'Logo';
    const isPercentage = type === 'Percentage' || type === 'Percent';

    if (isLogo) {
        // Handle different styling patterns:
        // 1. M12Hero5050Module: styles.watermarkLogo
        // 2. Card: styles.watermark + styles.logo
        // 3. Default: className
        const logoClassName = clsx(
            styles.watermarkLogo || styles.logo, // Specific logo class
            styles.watermark, // Base watermark class (for Card pattern)
            className, // Additional className
        );
        return <WatermarkLogo className={logoClassName} {...rest} />;
    }

    if (isPercentage) {
        // Handle different styling patterns:
        // 1. M12Hero5050Module: styles.watermarkPercent
        // 2. Card: styles.watermark + styles.markPercentage
        // 3. Default: className
        const percentClassName = clsx(
            styles.watermarkPercent || styles.markPercentage, // Specific percentage class
            styles.watermark, // Base watermark class (for Card pattern)
            className, // Additional className
        );
        return <WatermarkPercent className={percentClassName} {...rest} />;
    }

    // Default fallback - return null for unknown types
    return null;
};

export default Watermark;
